import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterList from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { getStatusColor, StatusLegend } from "../../utils/StatusChip";
import { CLASS_OPTIONS, STATUS_OPTIONS } from "../../utils/constants";
import { SLOT_LABELS } from "../../utils/sprintSlots";

const DEFAULT_FILTERS = {
  taskClass: "",
  doDateFrom: "",
  doDateTo: "",
  sprint: "",
  status: [],
  statusOperator: "contains",
  project: "",
  epic: "",
  track: "",
};

// Controlled task filter bar. `onChange` receives a partial filters patch; the
// sort control renders only when both `sortCriteria` and `onSortChange` are given.
const TaskFilterControls = ({
  filters: filtersProp,
  onChange,
  sprintOptions = [],
  projectOptions = [],
  epicOptions = [],
  trackOptions = [],
  sortCriteria,
  onSortChange,
  sortFields = [],
  showLegend = true,
}) => {
  const filters = { ...DEFAULT_FILTERS, ...filtersProp };
  const {
    taskClass,
    doDateFrom,
    doDateTo,
    sprint,
    status,
    statusOperator,
    project,
    epic,
    track,
  } = filters;

  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [anchorSort, setAnchorSort] = useState(null);
  const statusButtonRef = useRef(null);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState([]);
  const [draftStatusOperator, setDraftStatusOperator] = useState("contains");

  const set = (patch) => onChange?.(patch);

  const showSort = Array.isArray(sortCriteria) && typeof onSortChange === "function";
  const sortFieldList =
    sortFields.length > 0
      ? sortFields
      : [
          "line_up",
          "do_date",
          "status",
          "project",
          "epic",
          "track",
          "sprint",
          "estimated_time",
          "total_time_taken",
        ].map((f) => ({ value: f, label: f.replace(/_/g, " ") }));

  const openStatusMenu = () => {
    setDraftStatus(status);
    setDraftStatusOperator(statusOperator);
    setStatusMenuOpen(true);
  };

  const addSortCriterion = () =>
    onSortChange([...(sortCriteria || []), { field: sortFieldList[0].value, dir: "asc" }]);
  const removeSortCriterion = (index) =>
    onSortChange(sortCriteria.filter((_, i) => i !== index));
  const updateSortCriterion = (index, field, dir) =>
    onSortChange(sortCriteria.map((item, i) => (i === index ? { field, dir } : item)));

  const chips = [];
  if (taskClass) chips.push({ key: "taskClass", label: `Class: ${taskClass}`, onRemove: () => set({ taskClass: "" }) });
  if (status.length > 0) {
    const opLabel = statusOperator === "contains" ? "is" : statusOperator.replace("_", " ");
    chips.push({ key: "status", label: `Status ${opLabel}: ${status.join(", ")}`, onRemove: () => set({ status: [] }) });
  }
  if (sprint) chips.push({ key: "sprint", label: `Sprint: ${SLOT_LABELS[sprint] || sprint}`, onRemove: () => set({ sprint: "" }) });
  if (doDateFrom) chips.push({ key: "doDateFrom", label: `From: ${doDateFrom}`, onRemove: () => set({ doDateFrom: "" }) });
  if (doDateTo) chips.push({ key: "doDateTo", label: `To: ${doDateTo}`, onRemove: () => set({ doDateTo: "" }) });
  if (epic) {
    const name = epicOptions.find((e) => String(e.id) === String(epic))?.name || epic;
    chips.push({ key: "epic", label: `Epic: ${name}`, onRemove: () => set({ epic: "" }) });
  }
  if (project) {
    const name = projectOptions.find((p) => String(p.id) === String(project))?.name || project;
    chips.push({ key: "project", label: `Project: ${name}`, onRemove: () => set({ project: "" }) });
  }
  if (track) {
    const name = trackOptions.find((t) => String(t.id) === String(track))?.name || track;
    chips.push({ key: "track", label: `Track: ${name}`, onRemove: () => set({ track: "" }) });
  }

  const clearAllFilters = () =>
    set({ taskClass: "", status: [], sprint: "", doDateFrom: "", doDateTo: "", epic: "", project: "", track: "" });

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Task Class</InputLabel>
          <Select value={taskClass} label="Task Class" onChange={(e) => set({ taskClass: e.target.value })}>
            <MenuItem value=""><em>None</em></MenuItem>
            {CLASS_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          ref={statusButtonRef}
          variant="outlined"
          size="small"
          onClick={openStatusMenu}
          startIcon={<FilterList />}
          sx={{ minWidth: 120, height: 40 }}
        >
          Status {status.length > 0 && `(${status.length})`}
        </Button>

        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel>Sprint</InputLabel>
          <Select value={sprint} label="Sprint" onChange={(e) => set({ sprint: e.target.value })}>
            <MenuItem value=""><em>Any</em></MenuItem>
            {sprintOptions.map((sp) => (
              <MenuItem key={sp.value} value={sp.value}>{sp.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant="text"
          startIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setShowMoreFilters((v) => !v)}
        >
          {showMoreFilters ? "Fewer filters" : "More filters"}
        </Button>

        {showSort && (
          <Tooltip title="Sort">
            <IconButton size="small" onClick={(e) => setAnchorSort(e.currentTarget)}>
              <SortIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Collapse in={showMoreFilters}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mt: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>Do Date</Typography>
            <TextField
              label="From"
              type="date"
              size="small"
              value={doDateFrom}
              onChange={(e) => set({ doDateFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={doDateTo}
              onChange={(e) => set({ doDateTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          </Box>

          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel>Epic</InputLabel>
            <Select value={epic} label="Epic" onChange={(e) => set({ epic: e.target.value })}>
              <MenuItem value=""><em>Any</em></MenuItem>
              {epicOptions.map((ei) => (
                <MenuItem key={ei.id} value={ei.id}>{ei.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel>Project</InputLabel>
            <Select value={project} label="Project" onChange={(e) => set({ project: e.target.value })}>
              <MenuItem value=""><em>Any</em></MenuItem>
              {projectOptions.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel>Track</InputLabel>
            <Select value={track} label="Track" onChange={(e) => set({ track: e.target.value })}>
              <MenuItem value=""><em>Any</em></MenuItem>
              {trackOptions.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Collapse>

      {chips.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 1.5 }}>
          {chips.map((chip) => (
            <Chip key={chip.key} size="small" label={chip.label} onDelete={chip.onRemove} />
          ))}
          <Button size="small" variant="text" color="inherit" onClick={clearAllFilters}>
            Clear all
          </Button>
        </Box>
      )}

      {showLegend && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>Status:</Typography>
          <StatusLegend />
        </Box>
      )}

      {showSort && (
        <Menu anchorEl={anchorSort} open={Boolean(anchorSort)} onClose={() => setAnchorSort(null)}>
          <Box sx={{ px: 2, py: 1, minWidth: 300, maxWidth: 400 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Sort Criteria</Typography>
            {(sortCriteria || []).map((criterion, index) => (
              <Box
                key={index}
                sx={{ mb: 2, pb: 2, borderBottom: index < sortCriteria.length - 1 ? "1px solid #e0e0e0" : "none" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Sort Level {index + 1}</Typography>
                  <IconButton size="small" onClick={() => removeSortCriterion(index)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Field</InputLabel>
                  <Select
                    value={criterion.field}
                    label="Field"
                    onChange={(e) => updateSortCriterion(index, e.target.value, criterion.dir)}
                  >
                    {sortFieldList.map((f) => (
                      <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ToggleButtonGroup
                  value={criterion.dir}
                  exclusive
                  onChange={(e, v) => v && updateSortCriterion(index, criterion.field, v)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="asc">Ascending</ToggleButton>
                  <ToggleButton value="desc">Descending</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} size="small" fullWidth variant="outlined" onClick={addSortCriterion} sx={{ mt: 1 }}>
              Add Sort Level
            </Button>
          </Box>
        </Menu>
      )}

      <Popover
        anchorEl={statusButtonRef.current}
        open={statusMenuOpen}
        onClose={() => setStatusMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Status Filter</Typography>
          <Typography variant="caption" sx={{ mb: 1, display: "block" }}>Operator</Typography>
          <ToggleButtonGroup
            value={draftStatusOperator}
            exclusive
            onChange={(e, value) => {
              if (!value) return;
              setDraftStatusOperator(value);
              if (value === "equals" && draftStatus.length > 1) setDraftStatus([draftStatus[0]]);
            }}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="contains">Contains</ToggleButton>
            <ToggleButton value="equals">Equals</ToggleButton>
            <ToggleButton value="excludes">Excludes</ToggleButton>
            <ToggleButton value="not_contains">Doesn't Contain</ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="caption" sx={{ mb: 1, display: "block" }}>Status Values</Typography>
          <List dense sx={{ maxHeight: 260, overflow: "auto" }}>
            {STATUS_OPTIONS.map((value) => {
              const isSelected = draftStatus.includes(value);
              const colors = getStatusColor(value);
              return (
                <ListItemButton
                  key={value}
                  onClick={() => {
                    if (draftStatusOperator === "equals") {
                      setDraftStatus(isSelected ? [] : [value]);
                    } else {
                      setDraftStatus(
                        isSelected ? draftStatus.filter((s) => s !== value) : [...draftStatus, value],
                      );
                    }
                  }}
                  sx={{ mb: 0.5, borderRadius: 1, backgroundColor: colors.bg, color: colors.text }}
                >
                  {draftStatusOperator !== "equals" && <Checkbox checked={isSelected} size="small" />}
                  <ListItemText primary={value} />
                </ListItemButton>
              );
            })}
          </List>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button fullWidth size="small" onClick={() => setDraftStatus([])}>Clear</Button>
            <Button
              fullWidth
              size="small"
              variant="contained"
              onClick={() => {
                set({ status: draftStatus, statusOperator: draftStatusOperator });
                setStatusMenuOpen(false);
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export { DEFAULT_FILTERS };
export default TaskFilterControls;
